import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { api } from "../../api/client";
import { btnPrimary, type as text, theme } from "../../theme";
import { SheetCard, StatusTag, type StatusTone } from "../../components/Drawing";
import { LoadingBlock } from "../../components/Spinner";
import { NextSteps } from "../../components/NextSteps";
import { EntityTagsRow } from "../../components/EntityTags";
import { useDeepLinkScroll } from "../../hooks/useDeepLinkScroll";
import { EmptyState, Field, ModalHeader, ModalOverlay, input, textarea, secondaryBtn, dangerBtn, toDateInput, dateInputToIso, fmtDateOnly, todayInput } from "../resilience/shared";
import { AttentionList, FrameworkPick, Meta, OwnerField, attentionFor, msRef, personName, standardsOf, useMembers, useMsEnrolment, useMsPermissions, words, type Person } from "./shared";
import { attempt, useClearErrorOnDialog, useMsOverview, useRegister } from "./useRegister";

/** A result typed by a person, or recorded monthly by the platform (`value` null = a month without data). */
interface Result { id: string; measuredAt: string; value: number | null; note: string | null; source: "person" | "platform"; measureKey: string | null; period: string | null; gapReason: string | null }
/** A platform measure from the server's catalogue (backend/src/lib/objectiveMeasures.ts). */
interface Measure { key: string; label: string; unit: string; better: "higher" | "lower"; kind: "period" | "snapshot"; source: string; href: string; describes: string }
interface Objective {
  id: string; displayId: number | null; frameworkIds: string[]; title: string; description: string | null; measure: string; unit: string | null;
  direction: "at_least" | "at_most"; baseline: number | null; target: number | null; targetDate: string | null; frequency: string;
  resources: string | null; evaluationMethod: string | null; ownerUserId: string | null; owner: Person | null; ownerName: string | null; status: string;
  platformMeasure: string | null; platformMeasureSince: string | null;
  results: Result[];
  progress: { state: string; latest: { value: number; measuredAt: string } | null; nextMeasurementAt: string; measurementOverdue: boolean };
}

const STATE: Record<string, { tone: StatusTone; label: string }> = {
  met: { tone: "met", label: "Target met" },
  on_track: { tone: "partial", label: "On track" },
  off_track: { tone: "gap", label: "Moving away" },
  missed: { tone: "gap", label: "Missed" },
  no_data: { tone: "none", label: "No result yet" },
  no_target: { tone: "none", label: "No target" },
};

type Form = {
  frameworkIds: string[]; title: string; description: string; measure: string; unit: string; direction: string; baseline: string; target: string;
  targetDate: string; frequency: string; resources: string; evaluationMethod: string; ownerUserId: string; ownerName: string; status: string;
  platformMeasure: string;
};
const blank: Form = {
  frameworkIds: [], title: "", description: "", measure: "", unit: "", direction: "at_least", baseline: "", target: "", targetDate: "",
  frequency: "quarterly", resources: "", evaluationMethod: "", ownerUserId: "", ownerName: "", status: "active", platformMeasure: "",
};
const num = (v: string) => (v.trim() === "" ? null : Number(v));
/** A figure as typed: never scientific notation ("1e+21"), thousands separated. */
const figure = (n: number) => n.toLocaleString("en-GB", { maximumFractionDigits: 6 });

export function ObjectivesRegister() {
  const { data, loading, error, reload } = useRegister<{ objectives: Objective[]; vocabulary?: { measures?: Measure[] } }>("/management-system/objectives");
  const overview = useMsOverview();
  const enrolled = useMsEnrolment();
  const members = useMembers();
  const { canWrite } = useMsPermissions();
  const [params] = useSearchParams();
  const [editing, setEditing] = useState<{ id: string | null; form: Form } | null>(null);
  const [recording, setRecording] = useState<{ objective: Objective; measuredAt: string; value: string; note: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  useDeepLinkScroll(params.get("objective"), !!data && !overview.loading);
  useClearErrorOnDialog(setFormError, !!editing, !!recording);

  if (loading) return <LoadingBlock />;
  if (error || !data) return <p style={{ ...text.body, color: theme.color.dangerText }}>{error}</p>;
  const refresh = () => Promise.all([reload(), overview.reload()]);
  const measures = data.vocabulary?.measures ?? [];
  const measureOf = (key: string | null) => (key ? measures.find((m) => m.key === key) ?? null : null);

  const open = (o: Objective) => setEditing({
    id: o.id,
    form: {
      frameworkIds: o.frameworkIds, title: o.title, description: o.description ?? "", measure: o.measure, unit: o.unit ?? "", direction: o.direction,
      baseline: o.baseline?.toString() ?? "", target: o.target?.toString() ?? "", targetDate: toDateInput(o.targetDate), frequency: o.frequency,
      resources: o.resources ?? "", evaluationMethod: o.evaluationMethod ?? "", ownerUserId: o.ownerUserId ?? "", ownerName: o.ownerName ?? "", status: o.status,
      platformMeasure: o.platformMeasure ?? "",
    },
  });

  const save = async () => {
    if (!editing) return;
    const f = editing.form;
    const body = {
      ...f, description: f.description || null, unit: f.unit || null, baseline: num(f.baseline), target: num(f.target), targetDate: dateInputToIso(f.targetDate),
      resources: f.resources || null, evaluationMethod: f.evaluationMethod || null, ownerUserId: f.ownerUserId || null, ownerName: f.ownerName || null,
      platformMeasure: f.platformMeasure || null,
    };
    if (await attempt(() => (editing.id ? api.put(`/management-system/objectives/${editing.id}`, body) : api.post("/management-system/objectives", body)), setFormError, "Could not save the objective")) {
      setEditing(null);
      await refresh();
    }
  };

  const record = async () => {
    if (!recording) return;
    const body = { measuredAt: dateInputToIso(recording.measuredAt), value: Number(recording.value), note: recording.note || null };
    if (await attempt(() => api.post(`/management-system/objectives/${recording.objective.id}/results`, body), setFormError, "Could not record the result")) {
      setRecording(null);
      await refresh();
    }
  };

  const removeResult = async (o: Objective, r: Result) => {
    if (!window.confirm(`Remove the result recorded on ${fmtDateOnly(r.measuredAt)} (${r.value != null ? figure(r.value) : "no data"}) for ${msRef("objective", o.displayId)}?`)) return;
    if (await attempt(() => api.del(`/management-system/objectives/${o.id}/results/${r.id}`), setFormError, "Could not remove the result")) await refresh();
  };

  const remove = async (o: Objective) => {
    if (!window.confirm(`Delete ${msRef("objective", o.displayId)} ${o.title} and its results?`)) return;
    if (await attempt(() => api.del(`/management-system/objectives/${o.id}`), setFormError, "Could not delete the objective")) await refresh();
  };

  const f = editing?.form;
  const set = (patch: Partial<Form>) => editing && setEditing({ ...editing, form: { ...editing.form, ...patch } });
  /**
   * Choosing a platform measure fills what the objective is measured by where nothing is typed yet,
   * and always sets the unit: the figure arrives in the measure's own unit (% or count).
   */
  const chooseMeasure = (key: string) => {
    if (!f) return;
    const m = measureOf(key);
    set(m ? {
      platformMeasure: key,
      measure: f.measure.trim() ? f.measure : m.label,
      unit: m.unit,
      direction: m.better === "lower" ? "at_most" : "at_least",
      frequency: "monthly",
    } : { platformMeasure: "" });
  };
  const chosen = f ? measureOf(f.platformMeasure) : null;

  return (
    <>
      <AttentionList items={attentionFor(overview.data?.attention ?? [], "ms_objective")} />
      {formError && !editing && !recording && <p style={{ ...text.body, color: theme.color.dangerText }}>{formError}</p>}
      <SheetCard label="Objectives" reference="6.2 / 9.1" actions={canWrite ? <button type="button" style={secondaryBtn} onClick={() => setEditing({ id: null, form: blank })}><Plus size={12} /> Objective</button> : undefined}>
        {data.objectives.length === 0 ? (
          <EmptyState
            title="No objectives recorded"
            detail="Set measurable objectives consistent with your policy: what will be done, the measure, the target, who owns it and by when. Then record results at the frequency you choose."
            action={canWrite ? <button type="button" style={btnPrimary} onClick={() => setEditing({ id: null, form: blank })}>Add an objective</button> : undefined}
          />
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {data.objectives.map((o) => {
              const st = STATE[o.progress.state] ?? STATE.no_data;
              const pm = measureOf(o.platformMeasure);
              return (
                <div key={o.id} data-deeplink={o.id} style={{ border: "1px solid var(--c-border)", borderRadius: 3, padding: "10px 12px", background: "var(--c-panel)", opacity: o.status === "withdrawn" ? 0.6 : 1 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                    <span style={{ ...text.ref, color: theme.color.ref }}>{msRef("objective", o.displayId)}</span>
                    <span style={text.itemTitle}>{o.title}</span>
                    <StatusTag tone={st.tone}>{st.label}</StatusTag>
                    {o.status !== "active" && <span style={text.meta}>{words(o.status)}</span>}
                    {o.progress.measurementOverdue && <StatusTag tone="gap">Result overdue</StatusTag>}
                    {canWrite && (
                      <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                        <button type="button" style={secondaryBtn} onClick={() => setRecording({ objective: o, measuredAt: todayInput(), value: "", note: "" })}>Record result</button>
                        <button type="button" style={secondaryBtn} onClick={() => open(o)}>Edit</button>
                        <button type="button" style={dangerBtn} onClick={() => void remove(o)}>Delete</button>
                      </span>
                    )}
                  </div>
                  <Meta>
                    <span>Measure: {o.measure}{o.unit ? ` (${o.unit})` : ""}</span>
                    <span>Target: {o.target != null ? `${o.direction === "at_most" ? "at most" : "at least"} ${figure(o.target)}` : "not set"}{o.targetDate ? ` by ${fmtDateOnly(o.targetDate)}` : ""}</span>
                    <span>Latest: {o.progress.latest ? `${figure(o.progress.latest.value)} on ${fmtDateOnly(o.progress.latest.measuredAt)}` : "none"}</span>
                    <span>Next result due: {fmtDateOnly(o.progress.nextMeasurementAt)}</span>
                    <span>Owner: {personName(o.owner, o.ownerName)}</span>
                    <span>Standards: {standardsOf(o.frameworkIds, enrolled)}</span>
                    {o.platformMeasure && (
                      <span>Measured monthly by the platform: {pm ? <Link to={pm.href} style={{ color: theme.color.ref }}>{pm.label}</Link> : words(o.platformMeasure)}{pm ? ` (${pm.source})` : ""}</span>
                    )}
                  </Meta>
                  {o.results.length > 0 && (
                    <div style={{ ...text.hint, marginTop: 4, display: "flex", flexWrap: "wrap", gap: "2px 10px", alignItems: "center" }}>
                      <span>Results:</span>
                      {o.results.slice(0, 8).map((r) => (
                        <span key={r.id} title={(r.value == null ? r.gapReason : r.note) ?? undefined}>
                          {fmtDateOnly(r.measuredAt)} {r.value != null ? figure(r.value) : <span style={{ color: theme.color.muted }}>no data</span>}
                          {r.source === "platform" && <span style={{ ...text.meta, marginLeft: 4 }}>measured</span>}
                          {canWrite && r.source !== "platform" && <button type="button" aria-label={`Remove the result of ${fmtDateOnly(r.measuredAt)}`} title="Remove this result" style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, padding: "0 0 0 4px" }} onClick={() => void removeResult(o, r)}>×</button>}
                        </span>
                      ))}
                    </div>
                  )}
                  <EntityTagsRow entity={{ kind: "ms_objective", id: o.id }} readOnly={!canWrite} style={{ marginTop: 6 }} />
                </div>
              );
            })}
          </div>
        )}
      </SheetCard>

      {recording && (
        <ModalOverlay onClose={() => setRecording(null)}>
          <ModalHeader title={`Record a result: ${msRef("objective", recording.objective.displayId)}`} onClose={() => setRecording(null)} />
          <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Measured on"><input type="date" style={input} max={todayInput()} value={recording.measuredAt} onChange={(e) => setRecording({ ...recording, measuredAt: e.target.value })} /></Field>
            <Field label={`Value${recording.objective.unit ? ` (${recording.objective.unit})` : ""}`}><input type="number" style={input} value={recording.value} onChange={(e) => setRecording({ ...recording, value: e.target.value })} /></Field>
            <Field label="Note" span2><textarea style={textarea} rows={2} value={recording.note} onChange={(e) => setRecording({ ...recording, note: e.target.value })} /></Field>
            {formError && <p style={{ gridColumn: "1 / -1", margin: 0, ...text.body, color: theme.color.dangerText }}>{formError}</p>}
            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" style={secondaryBtn} onClick={() => setRecording(null)}>Cancel</button>
              <button type="button" style={btnPrimary} disabled={recording.value.trim() === "" || !recording.measuredAt} onClick={() => void record()}>Record</button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {editing && f && (
        <ModalOverlay onClose={() => setEditing(null)}>
          <ModalHeader title={editing.id ? "Edit objective" : "Add objective"} onClose={() => setEditing(null)} />
          <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {/* Ticks as the form is filled in: what 6.2 expects of each objective (what, resources, who, when, how evaluated). */}
            <div style={{ gridColumn: "1 / -1" }}>
              <NextSteps items={[
                { done: !!f.title.trim(), label: "Name the objective" },
                { done: !!f.measure.trim(), label: "Say how it is measured", hint: "6.2 asks for objectives that are measurable where practicable." },
                { done: !!f.unit.trim(), label: "Give the unit of measure" },
                { done: f.baseline.trim() !== "", label: "Record the baseline" },
                { done: f.target.trim() !== "", label: "Set the target" },
                { done: !!f.targetDate, label: "Set the target date", hint: "6.2: when it will be completed." },
                { done: !!f.resources.trim(), label: "Say what resources it needs" },
                { done: !!f.evaluationMethod.trim(), label: "Say how results are evaluated" },
                { done: !!(f.ownerUserId || f.ownerName.trim()), label: "Name an owner" },
              ]} />
            </div>
            <Field label="Objective" span2><input style={input} maxLength={300} value={f.title} onChange={(e) => set({ title: e.target.value })} /></Field>
            <Field label="What will be done" span2><textarea style={textarea} rows={2} value={f.description} onChange={(e) => set({ description: e.target.value })} /></Field>
            <Field label="Measured by the platform" span2 help={chosen ? `${chosen.describes} Recorded for each month the day after it ends; a month without data is shown as a gap. You can still record results yourself.` : "Optional. Pick a figure the platform already holds and it records a result for every month itself."}>
              <select style={input} value={f.platformMeasure} onChange={(e) => chooseMeasure(e.target.value)}>
                <option value="">No, results are recorded by hand</option>
                {measures.map((m) => <option key={m.key} value={m.key}>{m.label} ({m.source})</option>)}
              </select>
            </Field>
            <Field label="Measure" help="The indicator, e.g. percentage of staff trained."><input style={input} maxLength={300} value={f.measure} onChange={(e) => set({ measure: e.target.value })} /></Field>
            <Field label="Unit"><input style={input} value={f.unit} onChange={(e) => set({ unit: e.target.value })} placeholder="%" /></Field>
            <Field label="Target is">
              <select style={input} value={f.direction} onChange={(e) => set({ direction: e.target.value })}>
                <option value="at_least">At least the target (higher is better)</option>
                <option value="at_most">At most the target (lower is better)</option>
              </select>
            </Field>
            <Field label="Target"><input type="number" style={input} value={f.target} onChange={(e) => set({ target: e.target.value })} /></Field>
            <Field label="Baseline"><input type="number" style={input} value={f.baseline} onChange={(e) => set({ baseline: e.target.value })} /></Field>
            <Field label="Target date"><input type="date" style={input} value={f.targetDate} onChange={(e) => set({ targetDate: e.target.value })} /></Field>
            <Field label="Measured">
              <select style={input} value={f.frequency} onChange={(e) => set({ frequency: e.target.value })}>
                {["monthly", "quarterly", "biannual", "annual"].map((x) => <option key={x} value={x}>{x === "biannual" ? "Every six months" : words(x)}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select style={input} value={f.status} onChange={(e) => set({ status: e.target.value })}>
                {["active", "achieved", "missed", "withdrawn"].map((x) => <option key={x} value={x}>{words(x)}</option>)}
              </select>
            </Field>
            <Field label="Resources" span2><textarea style={textarea} rows={2} value={f.resources} onChange={(e) => set({ resources: e.target.value })} /></Field>
            <Field label="How results are evaluated" span2><textarea style={textarea} rows={2} value={f.evaluationMethod} onChange={(e) => set({ evaluationMethod: e.target.value })} /></Field>
            <OwnerField userId={f.ownerUserId} name={f.ownerName} members={members} onChange={(o) => set({ ownerUserId: o.userId, ownerName: o.name })} />
            <FrameworkPick allowed={enrolled} value={f.frameworkIds} onChange={(ids) => set({ frameworkIds: ids })} />
            {formError && <p style={{ gridColumn: "1 / -1", margin: 0, ...text.body, color: theme.color.dangerText }}>{formError}</p>}
            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" style={secondaryBtn} onClick={() => setEditing(null)}>Cancel</button>
              <button type="button" style={btnPrimary} disabled={!f.title.trim() || !f.measure.trim()} onClick={() => void save()}>Save</button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </>
  );
}
